import { useEffect, useRef } from "react";

interface KeyboardShortcutsProps {
  // Undo/Redo
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;

  // UI Controls
  onToggleSidebar?: () => void;
  onToggleTheme?: () => void;
  onPreview?: () => void;

  // Actions
  onSave?: () => void;
  onPublish?: () => void;

  // Device switching
  onDeviceDesktop?: () => void;
  onDeviceTablet?: () => void;
  onDeviceMobile?: () => void;

  // Help
  onShowKeyboardShortcuts?: () => void;
}

export const useKeyboardShortcuts = ({
  onUndo,
  onRedo,
  canUndo = false,
  canRedo = false,
  onToggleSidebar,
  onToggleTheme,
  onPreview,
  onSave,
  onPublish,
  onDeviceDesktop,
  onDeviceTablet,
  onDeviceMobile,
  onShowKeyboardShortcuts,
}: KeyboardShortcutsProps) => {
  // Use refs to store the latest function references
  const callbacksRef = useRef({
    onUndo,
    onRedo,
    onToggleSidebar,
    onToggleTheme,
    onPreview,
    onSave,
    onPublish,
    onDeviceDesktop,
    onDeviceTablet,
    onDeviceMobile,
    onShowKeyboardShortcuts,
  });

  // Update refs when callbacks change
  callbacksRef.current = {
    onUndo,
    onRedo,
    onToggleSidebar,
    onToggleTheme,
    onPreview,
    onSave,
    onPublish,
    onDeviceDesktop,
    onDeviceTablet,
    onDeviceMobile,
    onShowKeyboardShortcuts,
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {

      // Check if we're in an input field or textarea
      const target = event.target as HTMLElement;
      const isInputField =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.contentEditable === "true";

      // Don't trigger shortcuts when typing in input fields
      if (isInputField) {
        return;
      }

      const isMac =
        typeof navigator !== "undefined" && navigator.platform.includes("Mac");
      const cmdOrCtrl = isMac ? event.metaKey : event.ctrlKey;

      // Undo: Ctrl+Z (Windows/Linux) or Cmd+Z (Mac)
      if (cmdOrCtrl && event.key === "z" && !event.shiftKey) {
        event.preventDefault();
        if (canUndo && callbacksRef.current.onUndo) {
          callbacksRef.current.onUndo();
        }
        return;
      }

      // Redo: Ctrl+Y (Windows/Linux) or Cmd+Shift+Z (Mac)
      if (
        (event.ctrlKey && event.key === "y") ||
        (event.metaKey && event.key === "z" && event.shiftKey)
      ) {
        event.preventDefault();
        if (canRedo && callbacksRef.current.onRedo) {
          callbacksRef.current.onRedo();
        }
        return;
      }

      // Save as Draft: Ctrl+Shift+S (Windows/Linux) or Cmd+Shift+S (Mac)
      if (cmdOrCtrl && event.shiftKey && event.key === "S") {
        event.preventDefault();
        if (callbacksRef.current.onSave) {
          callbacksRef.current.onSave();
        }
        return;
      }

      // Publish: Ctrl+Shift+P (Windows/Linux) or Cmd+Shift+P (Mac)
      if (cmdOrCtrl && event.shiftKey && event.key === "P") {
        event.preventDefault();
        if (callbacksRef.current.onPublish) {
          callbacksRef.current.onPublish();
        }
        return;
      }

      // Preview: Ctrl+Shift+E (Windows/Linux) or Cmd+Shift+E (Mac)
      if (cmdOrCtrl && event.shiftKey && event.key === "E") {
        event.preventDefault();
        if (callbacksRef.current.onPreview) {
          callbacksRef.current.onPreview();
        }
        return;
      }

      // Toggle Sidebar: Ctrl+Shift+B (Windows/Linux) or Cmd+Shift+B (Mac)
      if (cmdOrCtrl && event.shiftKey && event.key === "B") {
        event.preventDefault();
        if (callbacksRef.current.onToggleSidebar) {
          callbacksRef.current.onToggleSidebar();
        }
        return;
      }

      // Toggle Theme: Ctrl+Alt+T (Windows/Linux) or Cmd+Alt+T (Mac)
      if (cmdOrCtrl && event.altKey && event.key === "t") {
        event.preventDefault();
        if (callbacksRef.current.onToggleTheme) {
          callbacksRef.current.onToggleTheme();
        }
        return;
      }

      // Device switching shortcuts
      // Desktop: Alt+1
      if (event.altKey && event.key === "1" && !cmdOrCtrl && !event.shiftKey) {
        event.preventDefault();
        if (callbacksRef.current.onDeviceDesktop) {
          callbacksRef.current.onDeviceDesktop();
        }
        return;
      }

      // Tablet: Alt+2
      if (event.altKey && event.key === "2" && !cmdOrCtrl && !event.shiftKey) {
        event.preventDefault();
        if (callbacksRef.current.onDeviceTablet) {
          callbacksRef.current.onDeviceTablet();
        }
        return;
      }

      // Mobile: Alt+3
      if (event.altKey && event.key === "3" && !cmdOrCtrl && !event.shiftKey) {
        event.preventDefault();
        if (callbacksRef.current.onDeviceMobile) {
          callbacksRef.current.onDeviceMobile();
        }
        return;
      }

      // Show keyboard shortcuts: Shift+? (which is Shift+/)
      if (event.key === "?" && event.shiftKey && !cmdOrCtrl && !event.altKey) {
        event.preventDefault();
        if (callbacksRef.current.onShowKeyboardShortcuts) {
          callbacksRef.current.onShowKeyboardShortcuts();
        }
        return;
      }
    };

    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [canUndo, canRedo]); // Only depend on boolean values, not functions
};
